import React, { useCallback, useEffect } from 'react'
import { FormProvider, useFormContext, UseFormReturn } from 'react-hook-form'
import { ButtonSpinner } from '@components/Login/LoginButton/Spinner/ButtonSpinner'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { SystemProgram, Transaction } from '@solana/web3.js'
import { Box, ErrorMessage, FancyButton, FormRender, Icon, Stack, Text, TextField } from '@ui'
import { formatUnits } from 'ethers/lib/utils'
import { formatUnitsToFixedLength } from 'hooks/useBalance'

import { confirmSolanaTx, useSolanaConnection } from '../../hooks/useSolanaConnection'
import { useSolanaWallet } from '../../hooks/useSolanaWallet'
import { Disclaimer } from '../../ui/Disclaimer'
import { TransferSuccessCallback } from '../types'
import { parseTransferAmount, toSolanaPublicKey } from '../utils'

import { SolanaTransferSchema, solanaTransferSchema } from './schema'

interface TransferSolanaFormProps {
    balance: bigint
    onTransferSuccess?: TransferSuccessCallback
}

export const TransferSolanaForm = ({ balance, onTransferSuccess }: TransferSolanaFormProps) => {
    return (
        <Stack grow>
            <FormRender
                gap
                grow
                schema={solanaTransferSchema}
                id="TransferSolanaFormx"
                mode="onChange"
            >
                {(form: UseFormReturn<SolanaTransferSchema>) => (
                    <FormProvider {...form}>
                        <Stack grow>
                            <TransferSolanaFormContent
                                balance={balance}
                                onTransferSuccess={onTransferSuccess}
                            />
                        </Stack>
                    </FormProvider>
                )}
            </FormRender>
        </Stack>
    )
}

const TransferSolanaFormContent = ({ balance, onTransferSuccess }: TransferSolanaFormProps) => {
    const { register, formState, watch, setValue, handleSubmit, reset } =
        useFormContext<SolanaTransferSchema>()

    const { errors } = formState
    const { solanaWallet } = useSolanaWallet()

    const amount = watch('amount')
    const recipient = watch('recipient')

    // always update balance so the schema can do correct validation
    useEffect(() => {
        setValue('balance', balance, { shouldValidate: false })
    }, [balance, setValue])

    const connection = useSolanaConnection()

    const onSend = useCallback(
        async (data: SolanaTransferSchema) => {
            let parsedAmount = parseTransferAmount({ value: data.amount, decimals: 9 })
            const parsedRecipient = toSolanaPublicKey(data.recipient)

            if (
                !parsedRecipient ||
                !solanaWallet ||
                !connection ||
                !balance ||
                parsedAmount < 5000n
            ) {
                return
            }

            // If the amount is the same as the balance, (the user selected max)
            // we need to subtract the fee or the transaction will fail
            // the transaction fee is 5000 lamports
            // https://solana.com/docs/core/fees#key-points
            if (parsedAmount === balance) {
                parsedAmount -= 5000n
            }

            try {
                const blockhash = (await connection.getLatestBlockhash('finalized')).blockhash

                const solanaPubKey = toSolanaPublicKey(solanaWallet.address)
                if (!solanaPubKey) {
                    return
                }
                const transaction = new Transaction().add(
                    SystemProgram.transfer({
                        fromPubkey: solanaPubKey,
                        toPubkey: parsedRecipient,
                        lamports: parsedAmount,
                    }),
                )
                transaction.recentBlockhash = blockhash
                transaction.feePayer = solanaPubKey

                const signedTx = await solanaWallet.signTransaction(transaction)
                const rawTransaction = signedTx.serialize()
                const signature = await connection.sendRawTransaction(rawTransaction, {
                    skipPreflight: true,
                    maxRetries: 5,
                })
                console.log('Transaction sent, signature:', signature)

                const status = await confirmSolanaTx(connection, signature)
                if (status.err) {
                    popupToast(({ toast }) => (
                        <StandardToast.Error
                            toast={toast}
                            message="Transaction failed during confirmation"
                        />
                    ))
                    return
                }

                await onTransferSuccess?.({
                    amount: data.amount,
                    symbol: 'SOL',
                    recipient: data.recipient,
                })
                reset()

                popupToast(({ toast }) => (
                    <StandardToast.Success
                        toast={toast}
                        message={`${data.amount} SOL was sent to ${data.recipient}`}
                        cta="View Transaction"
                        onCtaClick={() => {
                            window.open(`https://solscan.io/tx/${signature}`, '_blank')
                        }}
                    />
                ))
            } catch (error) {
                console.error(error)
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="An error occurred while sending SOL"
                    />
                ))
            }
        },
        [solanaWallet, connection, balance, onTransferSuccess, reset],
    )

    const onMaxClick = useCallback(() => {
        if (!balance) {
            return
        }
        const maxAmount = formatUnits(balance, 9)
        setValue('amount', maxAmount, { shouldValidate: true })
    }, [balance, setValue])

    const disabled = !recipient || !amount || !!errors.recipient || !!errors.amount || !solanaWallet

    return (
        <Stack grow gap="md">
            <Stack gap>
                <Stack gap="sm">
                    <TextField
                        autoFocus
                        renderLabel={(label) => (
                            <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                                <Text strong>{label}</Text>
                                <Stack
                                    cursor="pointer"
                                    data-testid="max-button"
                                    justifyContent="end"
                                    onClick={onMaxClick}
                                >
                                    <Text size="md" color="cta2">
                                        Enter Max
                                    </Text>
                                </Stack>
                            </Stack>
                        )}
                        after={<Text color="gray2">SOL</Text>}
                        background="level2"
                        label="Amount"
                        placeholder="Enter amount"
                        type="number"
                        min="0"
                        step="0.000000001"
                        tone={errors.amount ? 'error' : 'neutral'}
                        {...register('amount')}
                    />
                    <ErrorMessage preventSpace errors={errors} fieldName="amount" />
                </Stack>

                <Stack gap="sm">
                    <TextField
                        background="level2"
                        label="Send to"
                        placeholder="Enter Solana address"
                        tone={errors.recipient ? 'error' : 'neutral'}
                        {...register('recipient')}
                    />
                    <ErrorMessage preventSpace errors={errors} fieldName="recipient" />
                </Stack>
            </Stack>

            <Stack grow justifyContent="end" gap="sm">
                {!disabled && (
                    <Disclaimer>
                        Make sure the wallet you&apos;re sending to supports Solana.
                    </Disclaimer>
                )}
                <FancyButton
                    layoutRoot
                    cta
                    compact
                    spinner={formState.isSubmitting}
                    borderRadius="lg"
                    disabled={disabled}
                    onClick={handleSubmit(onSend)}
                >
                    {formState.isSubmitting ? `Sending ${amount} SOL` : 'Send'}
                </FancyButton>
            </Stack>
        </Stack>
    )
}

export const AssetBalanceRow = ({
    asset,
    balance = 0n,
    decimals,
    assetName,
}: {
    asset: 'base' | 'solana'
    balance?: bigint
    decimals?: number
    assetName: string
}) => {
    return !!decimals && !!balance ? (
        <Stack
            horizontal
            gap="sm"
            background="level2"
            padding="sm"
            rounded="md"
            alignItems="center"
        >
            <Box width="x4" height="x4" background="level1" rounded="full">
                <Icon type={asset === 'base' ? 'baseEth' : 'solana'} size="square_lg" />
            </Box>
            {balance !== undefined ? (
                <Text strong color="default" fontSize="lg">
                    {formatUnitsToFixedLength(balance, decimals)} {assetName}
                </Text>
            ) : (
                <ButtonSpinner />
            )}
        </Stack>
    ) : null
}
